/**
 * @engine/core - Lighting compute passes
 *
 * The Lighting-stage passes: collect scene lights, inject VFX particle lights, Forward+ tile culling, and
 * shadow-map computation. All four are pure compute (no color writes) and framebuffer-independent, which is why
 * the pipeline can run them before opaque geometry (one linear stage order for deferred / forward+ / unlit).
 *
 * Each pass owns its lazy GPU resource (the render2d compute-pass class) and publishes the live instance on the
 * blackboard so downstream sprite / accumulation / volumetric passes can read its buffers. This is where the
 * compute resources that were duplicated across EditorViewRenderer + GameViewRenderer now live once.
 *
 * @module @engine/core
 */

import { LightCullPass, ShadowMapPass, VFXLightInjectPass } from '@engine/render2d';
import { ViewportPass, RenderStage, BB, type ViewportPassContext } from '../RenderPipeline';

/** Collect scene lights → GPU (delegates to the deferred lighting resources' collect). */
export class CollectLightsPass extends ViewportPass {
  readonly stage = RenderStage.Lighting;
  override order = 0;
  readonly profileName = 'Render.LightCollect';

  execute(c: ViewportPassContext): void {
    c.lighting?.collect(c.camera, c.width, c.height);
  }
}

/** Inject light-emitting VFX particle systems into the light buffer (compute). */
export class InjectVfxLightsPass extends ViewportPass {
  readonly stage = RenderStage.Lighting;
  override order = 1;
  readonly profileName = 'Render.VFXLightInject';
  private pass: VFXLightInjectPass | null = null;

  execute(c: ViewportPassContext): void {
    if (!c.lighting) return;
    const collect = c.callbacks.vfxLightInject;
    if (!collect) return;
    const systems = collect();
    if (systems.length === 0) return;
    if (!this.pass && c.ctx.supportsCompute) this.pass = new VFXLightInjectPass(c.ctx);
    if (!this.pass?.isReady) return;
    const injected = this.pass.inject(c.lighting.lightManager, systems);
    if (injected > 0) c.lighting.lightManager.addVFXLightCount(injected);
  }
}

export class CullLightsPass extends ViewportPass {
  readonly stage = RenderStage.Lighting;
  override order = 2;
  readonly profileName = 'Render.LightCull';
  private pass: LightCullPass | null = null;

  execute(c: ViewportPassContext): void {
    if (c.lighting) {
      if (!this.pass && c.ctx.supportsCompute) this.pass = new LightCullPass(c.ctx);
      if (this.pass?.isReady) {
        this.pass.cull(c.camera, c.lighting.lightManager, c.width, c.height);
      }
    }
    // Publish even when null/not-ready: downstream passes treat a missing cull pass as "no Forward+ tiles".
    c.blackboard.set(BB.lightCull, this.pass);
  }
}

/** Shadow-map computation (compute) — traces rays from shadow-casting lights. Publishes the {@link ShadowMapPass}. */
export class ComputeShadowsPass extends ViewportPass {
  readonly stage = RenderStage.Lighting;
  override order = 3;
  readonly profileName = 'Render.Shadows';
  private pass: ShadowMapPass | null = null;

  execute(c: ViewportPassContext): void {
    const collect = c.callbacks.shadowCasterCollect;
    if (c.lighting && collect) {
      const casters = collect();
      if (!this.pass && c.ctx.supportsCompute) this.pass = new ShadowMapPass(c.ctx);
      if (this.pass?.isReady) {
        this.pass.beginFrame();
        for (const caster of casters) this.pass.addCaster(caster);
        this.pass.computeShadows(c.lighting.lightManager);
      }
    }
    // Publish the persistent instance (its shadowMapBuffer carries across frames, matching prior behavior).
    c.blackboard.set(BB.shadowMap, this.pass);
  }
}
